import React, { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Group,
  LoadingOverlay,
  Modal,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconCalculator,
  IconDownload,
  IconLock,
  IconLockOpen,
  IconPrinter,
  IconReportAnalytics,
} from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import usePermissions from "../../hooks/usePermissions";
import {
  CashDrawerSession,
  CashTransaction,
  CountDrawerResponse,
} from "../../types/cashDrawer";
import {
  downloadCsv,
  formatCurrency,
  formatDrawerDateTime,
  toCsv,
} from "../../utils/cashDrawerUtils";
import { CashAdjustmentModal } from "./CashAdjustmentModal";
import { CashDrawerBalancingSummaryModal } from "./CashDrawerBalancingSummaryModal";
import { CashDrawerPrintReport } from "./CashDrawerPrintReport";
import { CashTransactionsTable } from "./CashTransactionsTable";
import { CountDrawerModal } from "./CountDrawerModal";
import styles from "./CashDrawerPage.module.css";

type MetricTileKey =
  | "sales"
  | "paid_in"
  | "tips"
  | "refunds"
  | "expenses"
  | "deposits";

const METRIC_TILES: ReadonlyArray<{
  label: string;
  key: MetricTileKey;
  // Contra/refund tile: money out, rendered in accounting parentheses.
  contra?: boolean;
}> = [
  { label: "Cash/Check Sales", key: "sales" },
  { label: "Paid in", key: "paid_in" },
  { label: "Tips", key: "tips" },
  { label: "Refunds", key: "refunds", contra: true },
  { label: "Expenses", key: "expenses" },
  { label: "Deposits", key: "deposits" },
];

export const CashDrawerPage: React.FC = () => {
  const { getCashDrawerOverview, getCashDrawerSession, voidCashTransaction } =
    useApp();
  const { businessInfo } = useSalon() as {
    businessInfo: { id?: string; timezone?: string } | null;
  };
  const salonId = businessInfo?.id ?? "";
  const timezone = businessInfo?.timezone || "America/New_York";
  const perms = usePermissions();
  const canManage = perms?.cashRegister?.canManage ?? false;
  const isMobile = useMediaQuery("(max-width: 1024px)");

  const [loading, setLoading] = useState(false);
  const [currentSession, setCurrentSession] =
    useState<CashDrawerSession | null>(null);
  const [history, setHistory] = useState<CashDrawerSession[]>([]);
  const [viewingSession, setViewingSession] =
    useState<CashDrawerSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [countDrawerOpened, setCountDrawerOpened] = useState(false);
  const [adjustmentOpened, setAdjustmentOpened] = useState<null | "in" | "out">(
    null,
  );
  // Session UUID currently being summarized. Drives the full-screen
  // CashDrawerBalancingSummaryModal both for fresh closeouts and for
  // history rows the user clicks "View Summary" on.
  const [summarySessionId, setSummarySessionId] = useState<string | null>(null);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCashDrawerOverview(salonId);
      setCurrentSession(result.session);
      setHistory(result.history ?? []);
    } catch (e: any) {
      setError(e?.message || "Failed to load drawer");
      setCurrentSession(null);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [getCashDrawerOverview, salonId]);

  useEffect(() => {
    if (salonId) {
      setViewingSession(null);
      loadOverview();
    }
  }, [salonId, loadOverview]);

  const onTransactionRecorded = (_tx: CashTransaction) => {
    loadOverview();
  };

  const onCounted = (result: CountDrawerResponse) => {
    setCurrentSession(result.new_session);
    setViewingSession(null);
    loadOverview();
  };

  const handleStartFirstCount = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCashDrawerOverview(salonId, { ensureOpen: true });
      setCurrentSession(result.session);
      setViewingSession(null);
      setCountDrawerOpened(true);
    } catch (e: any) {
      setError(e?.message || "Failed to start drawer count");
    } finally {
      setLoading(false);
    }
  };

  const [voidConfirmTx, setVoidConfirmTx] = useState<CashTransaction | null>(
    null,
  );
  const [voiding, setVoiding] = useState(false);

  const handleVoid = (tx: CashTransaction) => {
    setVoidConfirmTx(tx);
  };

  const confirmVoid = async () => {
    if (!voidConfirmTx) return;
    setVoiding(true);
    try {
      await voidCashTransaction(voidConfirmTx.id);
      setVoidConfirmTx(null);
      loadOverview();
    } catch (e: any) {
      setError(e?.message || "Failed to void transaction");
    } finally {
      setVoiding(false);
    }
  };

  const loadHistoricalSession = async (session: CashDrawerSession) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCashDrawerSession(salonId, session.id);
      setViewingSession(result.session);
    } catch (e: any) {
      setError(e?.message || "Failed to load historical session");
    } finally {
      setLoading(false);
    }
  };

  const shownSession = viewingSession ?? currentSession;
  const isViewingHistorical = viewingSession !== null;
  const isOpen = shownSession?.status === "open";
  const metrics = shownSession?.metrics;
  const variance = shownSession?.variance;
  const hasCount =
    shownSession?.counted_balance !== null &&
    shownSession?.counted_balance !== undefined;
  const expectedCash = Number(shownSession?.expected_balance ?? 0);
  const expectedChecks = Number(metrics?.check_sales ?? 0);
  const expectedTotal = expectedCash + expectedChecks;
  const countedCash = Number(shownSession?.counted_balance ?? 0);
  const countedChecks = Number(shownSession?.counted_checks ?? 0);
  const countedTotal = countedCash + countedChecks;
  const salesTotal =
    Number(metrics?.cash_sales ?? 0) + Number(metrics?.check_sales ?? 0);
  const varianceColor =
    variance === null || variance === undefined || variance === 0
      ? undefined
      : variance > 0
        ? "green.7"
        : "red.7";
  const varianceLabel =
    variance === null || variance === undefined
      ? null
      : variance < 0
        ? "Short by"
        : variance > 0
          ? "Over by"
          : "Balanced";

  const handleExport = () => {
    if (!shownSession) return;
    downloadCsv(`cash-drawer-${shownSession.id}.csv`, toCsv(shownSession));
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <Box
        className={styles.pageRoot}
        // Fill the AppLayout content area; the inner column flex below
        // distributes header + sidebar + main exactly like the old modal.
        // Desktop has the navbar on the side (no top offset); mobile has a
        // 53px top navbar that AppLayout reserves via paddingTop.
        h={isMobile ? "calc(100dvh - 53px)" : "100dvh"}
      >
        <Flex
          direction="column"
          h="100%"
          mah="100%"
          className={styles.printHidden}
          style={{ overflow: "hidden" }}
        >
          <Group
            justify="space-between"
            wrap="nowrap"
            px="lg"
            py="md"
            style={{
              borderBottom: "1px solid var(--mantine-color-gray-2)",
              flex: "0 0 auto",
            }}
          >
            <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
              <Title order={3} fz={22} style={{ whiteSpace: "nowrap" }}>
                Cash Drawer
              </Title>
              <Text c="dimmed">·</Text>
              <Text fw={500} c="dimmed" truncate="end">
                {isViewingHistorical && viewingSession
                  ? formatDrawerDateTime(viewingSession.closed_at, timezone)
                  : "Current Drawer"}
              </Text>
            </Group>
            {shownSession && (
              <Group gap="xs" wrap="nowrap">
                {canManage && isOpen && !isViewingHistorical && (
                  <>
                    <Button
                      variant="light"
                      color="var(--brand-lime-green)"
                      onClick={() => setAdjustmentOpened("in")}
                    >
                      Pay In
                    </Button>
                    <Button
                      variant="light"
                      color="var(--brand-burnt-orange)"
                      onClick={() => setAdjustmentOpened("out")}
                    >
                      Pay Out
                    </Button>
                    <Button
                      color="var(--brand-teal)"
                      leftSection={<IconCalculator size={16} />}
                      onClick={() => setCountDrawerOpened(true)}
                    >
                      Count Drawer
                    </Button>
                  </>
                )}
                {isViewingHistorical && (
                  <Button
                    variant="light"
                    color="var(--brand-teal)"
                    leftSection={<IconReportAnalytics size={16} />}
                    onClick={() => setSummarySessionId(shownSession.id)}
                  >
                    View Summary
                  </Button>
                )}
              </Group>
            )}
          </Group>

          <Flex
            direction={isMobile ? "column" : "row"}
            flex={1}
            mih={0}
            style={{ overflow: "hidden" }}
          >
            <Paper
              component="aside"
              radius={0}
              bg="var(--mantine-color-gray-0)"
              aria-label="Cash drawer sessions"
              style={{
                flex: isMobile ? "0 0 auto" : "0 0 260px",
                maxHeight: isMobile ? 200 : undefined,
                width: isMobile ? "100%" : undefined,
                borderRight: isMobile
                  ? undefined
                  : "1px solid var(--mantine-color-gray-2)",
                borderBottom: isMobile
                  ? "1px solid var(--mantine-color-gray-2)"
                  : undefined,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <Text
                size="xs"
                tt="uppercase"
                fw={700}
                c="dimmed"
                px="md"
                py="sm"
                style={{
                  letterSpacing: "0.08em",
                  borderBottom: "1px solid var(--mantine-color-gray-2)",
                }}
              >
                Drawers
              </Text>
              <ScrollArea style={{ flex: 1 }} type="auto">
                <Stack gap={0} py="xs">
                  <UnstyledButton
                    onClick={() => setViewingSession(null)}
                    className={`${styles.sidebarItem} ${!isViewingHistorical ? styles.sidebarItemActive : ""}`}
                  >
                    <Text size="sm" fw={500} truncate="end">
                      Current Drawer
                      {currentSession?.auto_opened && " (auto-opened)"}
                    </Text>
                  </UnstyledButton>
                  {history.length > 0 && (
                    <>
                      <Text
                        size="xs"
                        tt="uppercase"
                        fw={600}
                        c="dimmed"
                        px="md"
                        pt="xs"
                        mt="xs"
                        style={{ letterSpacing: "0.08em" }}
                      >
                        Past sessions
                      </Text>
                      {history.map((s) => {
                        const isActive =
                          isViewingHistorical && viewingSession?.id === s.id;
                        return (
                          <UnstyledButton
                            key={s.id}
                            onClick={() => loadHistoricalSession(s)}
                            className={`${styles.sidebarItem} ${isActive ? styles.sidebarItemActive : ""}`}
                          >
                            <Text size="sm" fw={500} truncate="end">
                              {formatDrawerDateTime(s.closed_at, timezone)}
                            </Text>
                          </UnstyledButton>
                        );
                      })}
                    </>
                  )}
                </Stack>
              </ScrollArea>
            </Paper>

            <ScrollArea style={{ flex: 1, minWidth: 0 }} type="auto">
              <Box pos="relative" mih={200} p="lg">
                <LoadingOverlay visible={loading} zIndex={10} />

                {error && (
                  <Alert color="red" variant="light" mb="md">
                    {error}
                  </Alert>
                )}

                {isViewingHistorical && shownSession && (
                  <Alert
                    color="var(--brand-burnt-orange)"
                    variant="light"
                    mb="md"
                  >
                    <Text size="sm">
                      Read-only view of a past drawer session.
                    </Text>
                  </Alert>
                )}

                {!shownSession && !loading && (
                  <Alert color="gray" variant="light" mb="md">
                    <Stack gap="sm">
                      <Text size="sm">
                        No open drawer session yet. Click Count Drawer or make a
                        cash sale to open your first drawer.
                      </Text>
                      {canManage && (
                        <Button
                          color="var(--brand-teal)"
                          onClick={handleStartFirstCount}
                          size="sm"
                          style={{ alignSelf: "flex-start" }}
                        >
                          Count Drawer
                        </Button>
                      )}
                    </Stack>
                  </Alert>
                )}

                {shownSession && (
                  <Stack gap="md">
                    <Grid gutter="md">
                      <Grid.Col span={{ base: 12, md: 5 }}>
                        <Paper
                          withBorder
                          p="md"
                          h="100%"
                          style={{
                            borderLeft: "4px solid var(--mantine-color-gray-4)",
                          }}
                        >
                          <Group gap="xs" mb={6}>
                            <Text
                              size="xs"
                              tt="uppercase"
                              fw={600}
                              c="dimmed"
                              style={{ letterSpacing: "0.08em" }}
                            >
                              Opening Balance
                            </Text>
                            <Badge
                              size="sm"
                              color={
                                isOpen
                                  ? "var(--brand-teal)"
                                  : "var(--brand-burnt-orange)"
                              }
                              leftSection={
                                isOpen ? (
                                  <IconLockOpen size={12} />
                                ) : (
                                  <IconLock size={12} />
                                )
                              }
                            >
                              {isOpen ? "Open" : "Closed"}
                            </Badge>
                            {shownSession.auto_opened && isOpen && (
                              <Badge
                                size="sm"
                                color="var(--brand-lime-green)"
                                variant="light"
                              >
                                Auto-opened
                              </Badge>
                            )}
                          </Group>
                          <Text fz={32} fw={700} lh={1.1}>
                            {formatCurrency(shownSession.opening_balance)}
                          </Text>
                          {shownSession.opened_at && (
                            <Text size="xs" c="dimmed" mt={4}>
                              Opened{" "}
                              {formatDrawerDateTime(
                                shownSession.opened_at,
                                timezone,
                              )}
                            </Text>
                          )}
                          {shownSession.closed_at && (
                            <Text size="xs" c="dimmed" mt={2}>
                              Closed{" "}
                              {formatDrawerDateTime(
                                shownSession.closed_at,
                                timezone,
                              )}
                            </Text>
                          )}
                        </Paper>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 7 }}>
                        <Paper
                          withBorder
                          p="md"
                          h="100%"
                          style={{
                            borderLeft: "4px solid var(--brand-teal)",
                          }}
                        >
                          <Stack gap={8}>
                            <Group justify="space-between" wrap="nowrap">
                              <Text
                                size="xs"
                                tt="uppercase"
                                fw={700}
                                c="dimmed"
                                style={{ letterSpacing: "0.08em" }}
                              >
                                Expected in Drawer
                              </Text>
                              <Text fz={22} fw={700} lh={1.1}>
                                {formatCurrency(expectedTotal)}
                              </Text>
                            </Group>
                            <Group
                              justify="space-between"
                              wrap="nowrap"
                              pl="sm"
                            >
                              <Text size="sm" c="dimmed">
                                Cash
                              </Text>
                              <Text size="sm" fw={600}>
                                {formatCurrency(expectedCash)}
                              </Text>
                            </Group>
                            <Group
                              justify="space-between"
                              wrap="nowrap"
                              pl="sm"
                            >
                              <Text size="sm" c="dimmed">
                                Checks
                              </Text>
                              <Text size="sm" fw={600}>
                                {formatCurrency(expectedChecks)}
                              </Text>
                            </Group>
                            {hasCount && (
                              <>
                                <Divider variant="dashed" my={2} />
                                <Group justify="space-between" wrap="nowrap">
                                  <Text
                                    size="xs"
                                    tt="uppercase"
                                    fw={700}
                                    c="dimmed"
                                    style={{ letterSpacing: "0.08em" }}
                                  >
                                    Counted in Drawer
                                  </Text>
                                  <Text fz={22} fw={700} lh={1.1}>
                                    {formatCurrency(countedTotal)}
                                  </Text>
                                </Group>
                                <Group
                                  justify="space-between"
                                  wrap="nowrap"
                                  pl="sm"
                                >
                                  <Text size="sm" c="dimmed">
                                    Cash
                                  </Text>
                                  <Text size="sm" fw={600}>
                                    {formatCurrency(countedCash)}
                                  </Text>
                                </Group>
                                <Group
                                  justify="space-between"
                                  wrap="nowrap"
                                  pl="sm"
                                >
                                  <Text size="sm" c="dimmed">
                                    Checks
                                  </Text>
                                  <Text size="sm" fw={600}>
                                    {formatCurrency(countedChecks)}
                                  </Text>
                                </Group>
                              </>
                            )}
                            {hasCount &&
                              varianceLabel &&
                              variance !== null &&
                              variance !== undefined && (
                                <>
                                  <Divider variant="dashed" my={2} />
                                  <Group justify="space-between" wrap="nowrap">
                                    <Text
                                      size="xs"
                                      tt="uppercase"
                                      fw={600}
                                      c={varianceColor}
                                      style={{ letterSpacing: "0.08em" }}
                                    >
                                      {varianceLabel}
                                    </Text>
                                    <Text
                                      fz={22}
                                      fw={700}
                                      lh={1.1}
                                      c={varianceColor}
                                    >
                                      {variance === 0
                                        ? "—"
                                        : formatCurrency(Math.abs(variance))}
                                    </Text>
                                  </Group>
                                </>
                              )}
                          </Stack>
                        </Paper>
                      </Grid.Col>
                    </Grid>

                    {metrics && (
                      <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="sm">
                        {METRIC_TILES.map((m) => {
                          const value =
                            m.key === "sales" ? salesTotal : metrics[m.key];
                          return (
                            <Paper
                              key={m.key}
                              withBorder
                              p="sm"
                              bg="var(--mantine-color-gray-0)"
                            >
                              <Text
                                size="xs"
                                tt="uppercase"
                                fw={600}
                                c="dimmed"
                                style={{ letterSpacing: "0.06em" }}
                              >
                                {m.label}
                              </Text>
                              <Text fz={18} fw={600} mt={2}>
                                {formatCurrency(value, { contra: m.contra })}
                              </Text>
                            </Paper>
                          );
                        })}
                      </SimpleGrid>
                    )}

                    <Paper withBorder style={{ overflow: "hidden" }}>
                      <Group
                        justify="space-between"
                        wrap="nowrap"
                        px="md"
                        py="sm"
                        bg="var(--mantine-color-gray-0)"
                        style={{
                          borderBottom: "1px solid var(--mantine-color-gray-2)",
                        }}
                      >
                        <Text fw={700}>Transaction Log</Text>
                        <Group gap="xs">
                          <ActionIcon
                            variant="light"
                            color="var(--brand-teal)"
                            onClick={handlePrint}
                            aria-label="Print report"
                          >
                            <IconPrinter size={18} />
                          </ActionIcon>
                          <ActionIcon
                            variant="light"
                            color="var(--brand-teal)"
                            onClick={handleExport}
                            aria-label="Download CSV"
                          >
                            <IconDownload size={18} />
                          </ActionIcon>
                        </Group>
                      </Group>
                      <Box p="md">
                        {(shownSession.transactions ?? []).length === 0 ? (
                          <Text c="dimmed" ta="center" py="xl">
                            No transactions recorded yet.
                          </Text>
                        ) : (
                          <CashTransactionsTable
                            transactions={shownSession.transactions ?? []}
                            openingBalance={
                              Number(shownSession.opening_balance) || 0
                            }
                            canVoid={
                              canManage && isOpen && !isViewingHistorical
                            }
                            onVoid={handleVoid}
                            timezone={timezone}
                          />
                        )}
                      </Box>
                    </Paper>
                  </Stack>
                )}
              </Box>
            </ScrollArea>
          </Flex>
        </Flex>

        {shownSession && (
          <CashDrawerPrintReport
            session={shownSession}
            className={styles.printOnly}
            timezone={timezone}
          />
        )}
      </Box>

      {canManage && currentSession && (
        <>
          <CountDrawerModal
            opened={countDrawerOpened}
            onClose={() => setCountDrawerOpened(false)}
            onCounted={onCounted}
            onViewSummary={(closedSession) =>
              setSummarySessionId(closedSession.id)
            }
            session={currentSession}
            timezone={timezone}
          />
          <CashAdjustmentModal
            opened={adjustmentOpened !== null}
            onClose={() => setAdjustmentOpened(null)}
            onRecorded={onTransactionRecorded}
            session={currentSession}
            direction={adjustmentOpened ?? "in"}
          />
        </>
      )}

      <CashDrawerBalancingSummaryModal
        opened={summarySessionId !== null}
        onClose={() => setSummarySessionId(null)}
        sessionId={summarySessionId}
        timezone={timezone}
      />

      <Modal
        opened={voidConfirmTx !== null}
        onClose={() => !voiding && setVoidConfirmTx(null)}
        title="Void transaction"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text>Void this transaction? This cannot be undone.</Text>
          <Group justify="space-between">
            <Button
              variant="default"
              onClick={() => setVoidConfirmTx(null)}
              disabled={voiding}
            >
              Cancel
            </Button>
            <Button color="red" onClick={confirmVoid} loading={voiding}>
              Void
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default CashDrawerPage;
